import { useState, useCallback } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatMsgLine, formatMsgPrefix, OUTPUT_MSG_TYPE_COLORS } from '../../Types/OutputMsgTypes';

/**
 * One clickable segment of a log line — click copies JUST this segment's text
 * (trimmed), not the whole line. Used for the time/cat prefix and each
 * pipe-delimited part of msg.text (id, label, action, geo, ...).
 * @param {{text: string}} props
 */
function OutputMsgSegment({ text }) {
    const [copied, setCopied] = useState(false);

    const handleClick = useCallback(function copySegment() {
        navigator.clipboard.writeText(text.trim());
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    }, [text]);

    return (
        <span className="omd-row-segment" onClick={handleClick} title={copied ? 'Copied!' : 'Click to copy'}>
            {text}
        </span>
    );
}

/**
 * OutputMsgRow — one plain-text log line, colored by message type. Every
 * segment (time+cat prefix, and each `|`-delimited part of msg.text) is
 * individually clickable to copy just that piece — handy for quickly
 * grabbing an id without the surrounding text. Hover also reveals a copy
 * icon for the whole line.
 * @param {{msg: {id:string, cat:string, type:string, text:string, timestamp:number}}} props
 */
export default function OutputMsgRow({ msg }) {
    const [copied, setCopied] = useState(false);
    const parts = msg.text.split('|');

    const handleCopy = useCallback(function copyLine() {
        navigator.clipboard.writeText(formatMsgLine(msg));
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    }, [msg]);

    return (
        <div className="omd-row" style={{ color: OUTPUT_MSG_TYPE_COLORS[msg.type] }}>
            <span className="omd-row-text">
                <OutputMsgSegment text={formatMsgPrefix(msg)} />{' '}
                {parts.map((part, index) => (
                    <span key={index}>
                        <OutputMsgSegment text={part} />
                        {index < parts.length - 1 && '|'}
                    </span>
                ))}
            </span>
            <ContentCopyIcon className="omd-row-copy" onClick={handleCopy} sx={{ fontSize: 13 }} titleAccess={copied ? 'Copied!' : 'Copy line'} />
        </div>
    );
}
